import React, { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from './AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { TableScroller } from '@/components/ui/table-scroller';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, TrendingUp, Wallet, Target, Users as UsersIcon, Sparkles, Loader2, Save, User as UserIcon, UserCog } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTabParam } from './shared/useTabParam';
import { errorMessage } from './errors';
import NumberInput from './shared/NumberInput';
import LongTextCell from '@/components/LongTextCell';
import { Button } from '../components/ui/button';

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt$c = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const roleLabel = { admin: 'Admin', loan_officer: 'Loan Officer', loan_officer_assistant: 'LO Assistant', processor: 'Processor', borrower: 'Borrower', realtor: 'Realtor' };
const roleColor = { admin: '#8b5cf6', loan_officer: '#070B5F', loan_officer_assistant: '#6BA82E', processor: '#f59e0b' };

export default function CommissionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canHaveTeam = user?.role === 'loan_officer' || user?.role === 'loan_officer_assistant';
  const [mine, setMine] = useState(null);
  const [teamView, setTeamView] = useState(null);  // { self, members, combined, has_team }
  const [scope, setScope] = useTabParam('solo', { valid: ['solo', 'team'] });   // ?tab=solo|team (ticket 81)
  const [team, setTeam] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [saving, setSaving] = useState(null);
  const [loadError, setLoadError] = useState(null);   // message of the last failed summary load

  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get('/commissions/summary');
      setMine(data);
    } catch (e) {
      setMine(null);
      setLoadError(errorMessage(e, 'Try again in a minute.'));
      return;
    }
    if (canHaveTeam) {
      try { const { data } = await api.get('/commissions/summary/team'); setTeamView(data); } catch {}
    }
    if (isAdmin) {
      try { const { data } = await api.get('/commissions/team'); setTeam(data); } catch {}
      try { const { data } = await api.get('/admin/loan-officers'); setOfficers(data); } catch {}
    }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const updateMember = async (userId, patch) => {
    setSaving(userId);
    try {
      await api.patch(`/admin/users/${userId}`, patch);
      toast({ title: 'Saved', description: 'Commission profile updated' });
      load();
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
    setSaving(null);
  };

  if (loadError) {
    return (
      <div className="p-4 md:p-8">
        <div role="alert" className="bg-red-50 border border-red-100 rounded-2xl p-12 text-center text-red-700" data-testid="commissions-error">
          <p className="text-sm">Couldn’t load commissions.</p>
          <p className="text-xs mt-1">{loadError}</p>
          <Button size="sm" variant="outline" className="mt-4" onClick={load} data-testid="commissions-retry">Retry</Button>
        </div>
      </div>
    );
  }
  if (!mine?.user) return <div className="p-10 text-slate-500">Loading commissions...</div>;

  // If Team view is active, use combined totals; otherwise show solo (mine)
  const hasTeam = teamView?.has_team;
  const isTeamView = scope === 'team' && hasTeam;
  const view = isTeamView ? teamView.combined : mine;
  const stageSource = isTeamView ? teamView.combined.by_stage : mine.by_stage;

  // When an assistant takes a split, show what each side keeps instead of the gross twice.
  const splitHint = view.loa_potential > 0
    ? `${isTeamView ? 'LO' : 'You'} keep ${fmt$(view.net_potential)} · LOA ${fmt$(view.loa_potential)}`
    : (isTeamView ? 'LO + LOA combined' : 'All open leads');

  const cards = [
    { label: 'Potential Commission', value: fmt$(view.potential_total), icon: Target, color: '#6BA82E', hint: splitHint },
    { label: 'Weighted Pipeline', value: fmt$(view.weighted_pipeline), icon: TrendingUp, color: '#070B5F', hint: 'Probability-weighted forecast' },
    { label: 'Active Loans', value: fmt$(view.active_commission), icon: Wallet, color: '#f59e0b', hint: `${view.active_count} in app/processing/approved` },
    { label: 'Earned YTD', value: fmt$(view.earned_ytd), icon: DollarSign, color: '#22c55e', hint: `${fmt$(view.earned_mtd)} this month • ${view.closed_won_count} closed` },
  ];

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Commissions</h1>
          <p className="text-slate-500 mt-1">Default rate: <b>{mine.user.default_bps} bps</b> ({(mine.user.default_bps / 100).toFixed(2)}%) — {roleLabel[mine.user.role]}</p>
        </div>
        {canHaveTeam && hasTeam && (
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="commissions-scope-toggle">
            <button
              data-testid="scope-solo-btn"
              onClick={() => setScope('solo')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-1.5 transition ${scope === 'solo' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              style={scope === 'solo' ? { background: '#070B5F' } : {}}
            >
              <UserIcon size={14} /> My Income
            </button>
            <button
              data-testid="scope-team-btn"
              onClick={() => setScope('team')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-1.5 transition ${scope === 'team' ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              style={scope === 'team' ? { background: '#6BA82E' } : {}}
            >
              <UserCog size={14} /> Team ({(teamView.members?.length || 0) + 1})
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: c.color + '15' }}>
                  <Icon size={20} style={{ color: c.color }} />
                </div>
                <span className="text-xs text-slate-400">{c.hint}</span>
              </div>
              <div className="mt-4 text-2xl font-serif font-bold" style={{ color: '#070B5F' }}>{c.value}</div>
              <div className="text-sm text-slate-500 mt-0.5">{c.label}</div>
            </div>
          );
        })}
      </div>

      <Tabs defaultValue="my">
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-4' : (isTeamView ? 'grid-cols-3' : 'grid-cols-2')} h-auto mb-4`}>
          <TabsTrigger value="my" className="py-2.5" data-testid="tab-by-stage">By Pipeline Stage</TabsTrigger>
          <TabsTrigger value="top" className="py-2.5" data-testid="tab-top-leads">Top Leads</TabsTrigger>
          {isTeamView && <TabsTrigger value="members" className="py-2.5" data-testid="tab-team-members"><UsersIcon size={13} className="mr-1.5" />Team Breakdown</TabsTrigger>}
          {isAdmin && <TabsTrigger value="team" className="py-2.5" data-testid="tab-admin-team"><UsersIcon size={13} className="mr-1.5" />Team & Rates</TabsTrigger>}
        </TabsList>

        <TabsContent value="my">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>
              {isTeamView ? 'Team Commission by Pipeline Stage' : 'Commission by Pipeline Stage'}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <th className="pb-2">Stage</th><th className="pb-2">Leads</th><th className="pb-2">Volume</th><th className="pb-2">Gross Commission</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stageSource).map(([stage, data]) => (
                  <tr key={stage} className="border-b border-slate-50">
                    <td className="py-2.5 font-semibold capitalize" style={{ color: '#070B5F' }}>{stage.replace('_',' ')}</td>
                    <td className="py-2.5 text-slate-600">{data.count}</td>
                    <td className="py-2.5 text-slate-600">{fmt$(data.volume)}</td>
                    <td className="py-2.5 font-serif font-bold" style={{ color: '#6BA82E' }}>{fmt$c(data.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="top">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>{isTeamView ? 'Top 10 Team Leads by Commission' : 'Top 10 Leads by Commission'}</div>
            {(() => {
              const topLeads = isTeamView
                ? [...(teamView.self.top_leads || []), ...teamView.members.flatMap(m => m.top_leads || [])].sort((a,b) => b.commission - a.commission).slice(0, 10)
                : mine.top_leads;
              if (!topLeads.length) return <div className="text-slate-400 text-sm py-8 text-center">No leads yet.</div>;
              return (
                <TableScroller><table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                      <th className="pb-2">Name</th><th className="pb-2">Stage</th><th className="pb-2">Loan Amount</th><th className="pb-2 pr-4 min-w-[4rem]">Prob</th><th className="pb-2">Weighted</th><th className="pb-2">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topLeads.map(l => (
                      <tr key={l.lead_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2.5 font-semibold" style={{ color: '#070B5F' }}>{l.name}</td>
                        <td className="py-2.5 text-xs uppercase" style={{ color: '#6BA82E' }}>{l.status.replace('_',' ')}</td>
                        <td className="py-2.5 text-slate-600">{fmt$(l.loan_amount)}</td>
                        <td className="py-2.5"><span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F3FAE8', color: '#6BA82E' }}>{l.probability_pct}%</span></td>
                        <td className="py-2.5 font-serif" style={{ color: '#070B5F' }}>{fmt$c(l.weighted_commission)}</td>
                        <td className="py-2.5 font-serif font-bold" style={{ color: '#6BA82E' }}>{fmt$c(l.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></TableScroller>
              );
            })()}
          </div>
        </TabsContent>

        {isTeamView && (
          <TabsContent value="members">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="team-members-breakdown">
              <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Income by Team Member</div>
              <div className="grid gap-3">
                <MemberCard summary={teamView.self} isSelf />
                {teamView.members.map(m => <MemberCard key={m.user.user_id} summary={m} />)}
              </div>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="team">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <TeamTile label="Firm Potential" value={fmt$(team?.firm_totals?.potential || 0)} />
                <TeamTile label="Firm Weighted" value={fmt$(team?.firm_totals?.weighted || 0)} />
                <TeamTile label="Firm Active" value={fmt$(team?.firm_totals?.active || 0)} />
                <TeamTile label="Firm YTD Earned" value={fmt$(team?.firm_totals?.earned_ytd || 0)} accent="#22c55e" />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Team Commission Rates</div>
              <div className="text-xs text-slate-500 mb-3">Set each user's default commission (in basis points). LO Assistants receive a split % of their linked LO's commission.</div>
              <TableScroller>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                      <th className="pb-2">Name</th><th className="pb-2">Role</th><th className="pb-2">Default BPS</th><th className="pb-2">LOA Split %</th><th className="pb-2">Linked LO</th><th className="pb-2">Potential</th><th className="pb-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(team?.team || []).map(m => (
                      <TeamRow key={m.user.user_id} member={m} officers={officers} onSave={updateMember} saving={saving === m.user.user_id} />
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function TeamTile({ label, value, accent = '#070B5F' }) {
  return <div className="bg-slate-50 rounded-xl p-4">
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-serif text-2xl font-bold mt-1" style={{ color: accent }}>{value}</div>
  </div>;
}

function MemberCard({ summary, isSelf }) {
  const role = summary.user.role;
  const accent = role === 'loan_officer_assistant' ? '#6BA82E' : '#070B5F';
  return (
    <div className="rounded-xl border border-slate-100 p-4 hover:shadow-sm transition" data-testid={`member-card-${summary.user.user_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold flex items-center gap-2" style={{ color: '#070B5F' }}>
            {summary.user.name}
            {isSelf && <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-bold" style={{ background: '#F3FAE8', color: '#6BA82E' }}>You</span>}
          </div>
          <div className="text-xs text-slate-500">{roleLabel[role]} • {summary.user.default_bps} bps</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-slate-400">Potential</div>
          <div className="font-serif text-xl font-bold" style={{ color: accent }}>{fmt$(summary.potential_total)}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div><div className="text-slate-400 uppercase tracking-widest">Weighted</div><div className="font-serif font-bold text-sm" style={{ color: '#070B5F' }}>{fmt$(summary.weighted_pipeline)}</div></div>
        <div><div className="text-slate-400 uppercase tracking-widest">Active</div><div className="font-serif font-bold text-sm" style={{ color: '#f59e0b' }}>{fmt$(summary.active_commission)}</div></div>
        <div><div className="text-slate-400 uppercase tracking-widest">YTD</div><div className="font-serif font-bold text-sm" style={{ color: '#22c55e' }}>{fmt$(summary.earned_ytd)}</div></div>
        <div><div className="text-slate-400 uppercase tracking-widest">Leads</div><div className="font-serif font-bold text-sm text-slate-700">{summary.total_leads}</div></div>
      </div>
    </div>
  );
}

function TeamRow({ member, officers, onSave, saving }) {
  const [role, setRole] = useState(member.user.role);
  const [bps, setBps] = useState(member.user.default_bps || 100);
  const [split, setSplit] = useState(0);
  const [linkedLo, setLinkedLo] = useState('');

  useEffect(() => {
    // Fetch full user record for LOA fields
    api.get('/admin/users').then(({ data }) => {
      const full = data.find(u => u.user_id === member.user.user_id);
      if (full) { setSplit(full.assistant_split_pct || 0); setLinkedLo(full.linked_lo_id || ''); }
    }).catch(() => {});
  }, [member.user.user_id]);

  const save = () => {
    // Only send role when it actually changed. The dropdown is seeded from
    // member.user.role, which for a tenant owner is the legacy users.role
    // "admin" while the real membership role is "owner" — sending
    // role: 'admin' unchanged trips the backend's last_owner 409 every time.
    const patch = { default_commission_bps: Number(bps) };
    if (role !== member.user.role) patch.role = role;
    if (role === 'loan_officer_assistant') {
      patch.assistant_split_pct = Number(split);
      patch.linked_lo_id = linkedLo || null;
    }
    onSave(member.user.user_id, patch);
  };

  return (
    <tr className="border-b border-slate-50">
      <td className="py-3">
        <LongTextCell id={`team-name-${member.user.user_id}`} text={member.user.name} lines={2} maxWidth={200} className="font-semibold text-[#070B5F]" />
        <div className="text-xs text-slate-500">{member.user.role}</div>
      </td>
      <td className="py-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="loan_officer">Loan Officer</SelectItem>
            <SelectItem value="loan_officer_assistant">LO Assistant</SelectItem>
            <SelectItem value="processor">Processor</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="py-3">
        <NumberInput integer value={bps} onChange={(n) => setBps(n ?? 0)} className="w-20" />
        <div className="text-[10px] text-slate-400">= {(bps / 100).toFixed(2)}%</div>
      </td>
      <td className="py-3">
        {role === 'loan_officer_assistant' ? (
          <div className="flex items-center gap-1">
            <NumberInput integer value={split} onChange={(n) => setSplit(n ?? 0)} className="w-16" />
            <span className="text-xs text-slate-500">%</span>
          </div>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="py-3">
        {role === 'loan_officer_assistant' ? (
          <Select value={linkedLo} onValueChange={setLinkedLo}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Select LO..." /></SelectTrigger>
            <SelectContent>
              {officers.map(o => <SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="py-3 font-serif font-bold" style={{ color: '#6BA82E' }}>{fmt$(member.potential_total)}</td>
      <td className="py-3">
        <button onClick={save} disabled={saving} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1" style={{ background: '#070B5F' }}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </td>
    </tr>
  );
}
